import type { Units } from "../lib/config";
import { fmtLD, fmtSink, fmtSpeed } from "../lib/format";
import { msToKt } from "../lib/units";
import type { PolarOutputs } from "../lib/outputs";

type CardProps = {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly accent: string;
  readonly testId: string;
};

function StatCard({ label, value, sub, accent, testId }: CardProps) {
  return (
    <div className="rounded-[10px] border border-border bg-panel px-3.5 py-3">
      <div className="text-[11px] uppercase tracking-wide text-muted">{label}</div>
      <div
        data-testid={testId}
        className={`mt-1 text-xl font-semibold tabular-nums ${accent}`}
      >
        {value}
      </div>
      <div className="mt-0.5 text-[11px] text-muted">{sub}</div>
    </div>
  );
}

const windWord = (windHeadwind_kmh: number): string =>
  windHeadwind_kmh >= 0 ? "headwind" : "tailwind";

const fmtMc = (mc_ms: number, units: Units): string =>
  units === "metric" ? `${mc_ms.toFixed(1)} m/s` : `${msToKt(mc_ms).toFixed(1)} kt`;

type StatCardsProps = {
  readonly outputs: PolarOutputs;
  readonly units: Units;
  readonly macCready_ms: number;
  readonly windHeadwind_kmh: number;
};

/** The four headline numbers. */
export function StatCards({
  outputs,
  units,
  macCready_ms,
  windHeadwind_kmh,
}: StatCardsProps) {
  const { minSink, bestLD, speedToFly, xcSpeed } = outputs;
  const wind = windHeadwind_kmh;

  return (
    <div className="mt-3.5 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
      <StatCard
        label="Min sink"
        value={fmtSink(minSink.w_ms, units)}
        sub={`at ${fmtSpeed(minSink.v_kmh, units)}`}
        accent="text-sink"
        testId="stat-minsink"
      />
      <StatCard
        label="Best L/D"
        value={fmtLD(bestLD.ld)}
        sub={`at ${fmtSpeed(bestLD.v_kmh, units)} · sink ${fmtSink(bestLD.w_ms, units)}`}
        accent="text-lift"
        testId="stat-bestld"
      />
      <StatCard
        label="Speed-to-fly"
        value={speedToFly !== null ? fmtSpeed(speedToFly.v_kmh, units) : "—"}
        sub={
          speedToFly !== null
            ? wind === 0
              ? `MC ${fmtMc(macCready_ms, units)}, no wind`
              : `MC ${fmtMc(macCready_ms, units)}, ${windWord(wind)} ${fmtSpeed(Math.abs(wind), units)}`
            : "MC tangent has no solution"
        }
        accent="text-stf"
        testId="stat-stf"
      />
      <StatCard
        label="XC speed (ground)"
        value={xcSpeed !== null ? fmtSpeed(xcSpeed.vGround_kmh, units) : "—"}
        sub={
          xcSpeed !== null
            ? wind === 0
              ? `tangent crosses at ${fmtSpeed(xcSpeed.vAir_kmh, units)}`
              : `${fmtSpeed(xcSpeed.vAir_kmh, units)} TAS − ${fmtSpeed(Math.abs(wind), units)} ${windWord(wind)}`
            : "MC tangent has no solution"
        }
        accent="text-xc"
        testId="stat-xc"
      />
    </div>
  );
}
